/**
 * ComposeModal — staff compose a new secure message.
 *
 * Features:
 *   - Typeahead contact search via /api/contacts
 *   - Subject + body textarea
 *   - File attachment (drag-drop or click, multiple, 25 MB each)
 *   - On send: POST /api/messages, then POST /api/messages/:id/attachments for each file
 *   - Shows copy-able magic link on success
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { messages as messagesApi, ghl, ghlContactsUrl, GHLContactResult, NotifiedVia } from '../../../lib/api.js';
import { useAuth } from '../../../lib/auth-context.js';

interface Props {
  onClose: () => void;
  onSent: () => void;
}

export default function ComposeModal({ onClose, onSent }: Props): React.ReactElement {
  const { user } = useAuth();
  const locationId = user?.locationId ?? '';
  const [contactQuery,   setContactQuery]   = useState('');
  const [contacts,       setContacts]       = useState<GHLContactResult[]>([]);
  const [noResults,      setNoResults]      = useState(false);
  const [selectedContact, setSelected]      = useState<GHLContactResult | null>(null);
  const [subject,        setSubject]        = useState('');
  const [body,           setBody]           = useState('');
  const [files,          setFiles]          = useState<File[]>([]);
  const [sending,        setSending]        = useState(false);
  const [magicLink,      setMagicLink]      = useState<string | null>(null);
  const [notifiedVia,    setNotifiedVia]    = useState<NotifiedVia>('none');
  const [error,          setError]          = useState<string | null>(null);
  const [contactLoading, setContactLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced contact search
  useEffect(() => {
    if (!contactQuery || contactQuery.length < 2) {
      setContacts([]);
      setNoResults(false);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setContactLoading(true);
      setNoResults(false);
      try {
        const res = await ghl.searchContacts(contactQuery);
        const found = res.success && res.data ? res.data.contacts : [];
        setContacts(found);
        setNoResults(found.length === 0);
      } catch {
        setContacts([]);
        setNoResults(true);
      } finally {
        setContactLoading(false);
      }
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [contactQuery]);

  const openAddContact = () => {
    if (!locationId) return;
    window.open(ghlContactsUrl(locationId), '_blank', 'noopener,noreferrer');
  };

  const handleFileChange = useCallback((incoming: FileList | null) => {
    if (!incoming) return;
    const added = Array.from(incoming).filter((f) => f.size <= 25 * 1024 * 1024);
    setFiles((prev) => [...prev, ...added]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileChange(e.dataTransfer.files);
  }, [handleFileChange]);

  const handleSend = async () => {
    if (!selectedContact) { setError('Please select a contact'); return; }
    if (!subject.trim())  { setError('Subject is required'); return; }
    if (!body.trim())     { setError('Message body is required'); return; }

    setSending(true);
    setError(null);
    try {
      const sendRes = await messagesApi.send({
        toContactGhlId: selectedContact.id,
        subject: subject.trim(),
        body: body.trim(),
      });

      if (!sendRes.success || !sendRes.data) {
        setError(sendRes.error ?? 'Failed to send message');
        return;
      }

      const { messageId, magicLink: link } = sendRes.data;
      setNotifiedVia(sendRes.data.notifiedVia ?? (sendRes.data.notified ? 'sms' : 'none'));

      // Upload attachments sequentially (keep it simple, show errors inline)
      for (const file of files) {
        const attachRes = await messagesApi.attach(messageId, file);
        if (!attachRes.success) {
          setError(`Warning: attachment "${file.name}" failed to upload. Message was sent.`);
        }
      }

      setMagicLink(link);
      onSent();
    } catch {
      setError('Unexpected error sending message');
    } finally {
      setSending(false);
    }
  };

  // Success state — show magic link
  if (magicLink) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>Message Sent ✓</h2>
            <button style={styles.closeBtn} onClick={onClose}>✕</button>
          </div>
          <div style={styles.body}>
            <p style={{ color: notifiedVia !== 'none' ? '#166534' : '#b45309', marginBottom: 12 }}>
              {notifiedVia === 'email_sms'
                ? 'The contact has been notified by email and text with a secure link.'
                : notifiedVia === 'email'
                ? 'The contact has been notified by email with a secure link.'
                : notifiedVia === 'sms'
                ? 'The contact has been notified by text with a secure link.'
                : 'We couldn’t notify the contact automatically. Copy the link below and send it to them directly.'}
            </p>
            <label style={styles.label}>Magic Link (copy if needed)</label>
            <div style={styles.linkRow}>
              <input readOnly value={magicLink} style={styles.linkInput} />
              <button
                style={styles.copyBtn}
                onClick={() => navigator.clipboard.writeText(magicLink)}
              >
                Copy
              </button>
            </div>
          </div>
          <div style={styles.footer}>
            <button style={styles.primary} onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>New Secure Message</h2>
          <button style={styles.closeBtn} onClick={onClose} disabled={sending}>✕</button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {error && <div style={styles.errorBanner}>{error}</div>}

          {/* Contact picker */}
          <div style={styles.field}>
            <label style={styles.label}>To</label>
            {selectedContact ? (
              <div style={styles.selectedContact}>
                <span>{selectedContact.firstName} {selectedContact.lastName} — {selectedContact.email}</span>
                <button style={styles.clearBtn} onClick={() => { setSelected(null); setContactQuery(''); }}>✕</button>
              </div>
            ) : (
              <>
                <input
                  style={styles.input}
                  placeholder="Search by name, email, or phone…"
                  value={contactQuery}
                  onChange={(e) => setContactQuery(e.target.value)}
                  disabled={sending}
                />
                {contactLoading && <p style={styles.hint}>Searching…</p>}
                {contacts.length > 0 && (
                  <div style={styles.dropdown}>
                    {contacts.map((c) => (
                      <button
                        key={c.id}
                        style={styles.dropdownItem}
                        onClick={() => { setSelected(c); setContactQuery(''); setContacts([]); setNoResults(false); }}
                      >
                        {c.firstName} {c.lastName}
                        <span style={styles.dropdownSub}>{c.email}</span>
                      </button>
                    ))}
                  </div>
                )}
                {!contactLoading && noResults && (
                  <div style={styles.noResults}>
                    <span style={styles.noResultsText}>No contact found for “{contactQuery.trim()}”.</span>
                    <button style={styles.addNewBtn} onClick={openAddContact} type="button" disabled={!locationId}>
                      + Add new contact
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Subject */}
          <div style={styles.field}>
            <label style={styles.label}>Subject</label>
            <input
              style={styles.input}
              maxLength={500}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject (max 500 characters)"
              disabled={sending}
            />
          </div>

          {/* Body */}
          <div style={styles.field}>
            <label style={styles.label}>Message</label>
            <textarea
              style={{ ...styles.input, minHeight: 160, resize: 'vertical' }}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your secure message here…"
              disabled={sending}
            />
          </div>

          {/* Attachments */}
          <div style={styles.field}>
            <label style={styles.label}>Attachments (PDF, JPG, PNG, DOCX — max 25 MB each)</label>
            <div
              style={styles.dropzone}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <span style={{ color: '#6b7280' }}>Drop files here or click to browse</span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleFileChange(e.target.files)}
              />
            </div>
            {files.length > 0 && (
              <ul style={styles.fileList}>
                {files.map((f, i) => (
                  <li key={i} style={styles.fileItem}>
                    <span>{f.name} ({(f.size / 1024).toFixed(0)} KB)</span>
                    <button
                      style={styles.clearBtn}
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                    >✕</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button style={styles.secondary} onClick={onClose} disabled={sending}>Cancel</button>
          <button style={styles.primary} onClick={handleSend} disabled={sending}>
            {sending ? 'Sending…' : '🔒 Send Securely'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position:   'fixed', inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display:    'flex', alignItems: 'center', justifyContent: 'center',
    zIndex:     1000,
  },
  modal: {
    background:   '#fff',
    borderRadius: 12,
    width:        '100%',
    maxWidth:     560,
    maxHeight:    '90vh',
    display:      'flex',
    flexDirection: 'column',
    boxShadow:    '0 20px 60px rgba(0,0,0,0.15)',
  },
  header: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    padding:        '16px 20px',
    borderBottom:   '1px solid #e5e7eb',
  },
  title: { margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 18, color: '#6b7280', padding: 4,
  },
  body: { flex: 1, overflowY: 'auto', padding: '16px 20px' },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: 8,
    padding: '12px 20px', borderTop: '1px solid #e5e7eb',
  },
  field: { marginBottom: 16 },
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 },
  input: {
    width: '100%', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 6,
    fontSize: 14, color: '#111827',
    boxSizing: 'border-box',
  },
  hint: { fontSize: 12, color: '#9ca3af', marginTop: 4 },
  dropdown: {
    border: '1px solid #d1d5db', borderRadius: 6,
    background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    maxHeight: 200, overflowY: 'auto',
    marginTop: 4,
  },
  dropdownItem: {
    display: 'flex', flexDirection: 'column',
    width: '100%', padding: '8px 12px',
    border: 'none', background: 'none', cursor: 'pointer',
    textAlign: 'left', fontSize: 14, color: '#111827',
  },
  dropdownSub: { fontSize: 12, color: '#6b7280' },
  noResults: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
    marginTop: 4, padding: '8px 12px',
    border: '1px solid #e5e7eb', borderRadius: 6, background: '#f9fafb',
  },
  noResultsText: { fontSize: 13, color: '#6b7280' },
  addNewBtn: {
    flexShrink: 0, background: '#eff6ff', color: '#2563eb',
    border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 12px',
    cursor: 'pointer', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap',
  },
  selectedContact: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 10px', background: '#eff6ff', borderRadius: 6,
    fontSize: 14, color: '#2563eb',
  },
  clearBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#6b7280', fontSize: 14, padding: 2,
  },
  dropzone: {
    border: '2px dashed #d1d5db', borderRadius: 8,
    padding: '24px 16px', textAlign: 'center',
    cursor: 'pointer', fontSize: 14,
  },
  fileList: { listStyle: 'none', padding: 0, marginTop: 8 },
  fileItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '4px 0', fontSize: 13, color: '#374151',
  },
  errorBanner: {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 6, padding: '8px 12px',
    color: '#b91c1c', fontSize: 13, marginBottom: 12,
  },
  primary: {
    background: '#2563eb', color: '#fff', border: 'none',
    borderRadius: 6, padding: '8px 20px', cursor: 'pointer',
    fontSize: 14, fontWeight: 500,
  },
  secondary: {
    background: '#f9fafb', color: '#374151',
    border: '1px solid #d1d5db', borderRadius: 6,
    padding: '8px 16px', cursor: 'pointer', fontSize: 14,
  },
  linkRow: { display: 'flex', gap: 8 },
  linkInput: {
    flex: 1, padding: '8px 10px', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 12, color: '#374151',
  },
  copyBtn: {
    padding: '8px 14px', background: '#f3f4f6', border: '1px solid #d1d5db',
    borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
};
